import { useState, useEffect, useRef } from 'react';
import { useSearchStore } from '../stores/searchStore';
import { Search, X } from 'lucide-react';

import { SearchResult } from '../lib/searchAPI';
import { AuthorizedMediaImage } from '../shared/media/authorizedMedia';

interface GlobalSearchProps {
  onNavigate?: (url: string) => void;
}

const ENTITY_TYPE_LABELS = {
  design: 'Дизайн',
  product: 'Товар PIM',
  wb_product: 'Товар WB',
  ozon_product: 'Товар Ozon',
  ym_product: 'Товар Я.Маркет',
  category: 'Категория',
  activity_log: 'Лог активности',
};

const ENTITY_TYPE_COLORS = {
  design: 'bg-purple-100 dark:bg-purple-950 text-purple-800 dark:text-purple-300',
  product: 'bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300',
  wb_product: 'bg-pink-100 dark:bg-pink-950 text-pink-800 dark:text-pink-300',
  ozon_product: 'bg-orange-100 dark:bg-orange-950 text-orange-800 dark:text-orange-300',
  ym_product: 'bg-yellow-100 dark:bg-yellow-950 text-yellow-800 dark:text-yellow-300',
  category: 'bg-green-100 dark:bg-green-950 text-green-800 dark:text-green-300',
  activity_log: 'bg-gray-100 dark:bg-zinc-800 text-gray-800 dark:text-zinc-200',
};

// Перевод названий полей на русский
const FIELD_LABELS: Record<string, string> = {
  title: 'Название',
  name: 'Название',
  article: 'Артикул',
  brand: 'Бренд',
  category: 'Категория',
  description: 'Описание',
  design_code: 'Код дизайна',
  type: 'Тип',
  theme: 'Тема',
  image_text: 'Текст на изображении',
  nm_id: 'nmID',
  vendor_code: 'Артикул продавца',
  prefix: 'Префикс',
  design_type: 'Тип дизайна',
  action: 'Действие',
  entity_name: 'Название сущности',
  entity_type: 'Тип сущности',
  user: 'Пользователь',
  user_name: 'Пользователь',
};

export default function GlobalSearch({ onNavigate }: GlobalSearchProps) {
  const [localQuery, setLocalQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  const { search, results, isSearching, totalResults, clearSearch, setPage } = useSearchStore();

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (localQuery.trim().length > 0) {
      debounceRef.current = setTimeout(() => {
        setPage(1); // Сбрасываем на первую страницу при новом поиске
        search(localQuery, 'all');
        setShowDropdown(true);
        setSelectedIndex(-1);
      }, 300);
    } else {
      clearSearch();
      setShowDropdown(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [localQuery]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleResultClick = (result: SearchResult) => {
    setShowDropdown(false);
    setLocalQuery('');
    clearSearch();
    if (onNavigate) {
      onNavigate(result.url);
    } else {
      window.location.hash = result.url;
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-800 dark:text-yellow-100 font-semibold">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div ref={searchRef} className="relative w-full">
      {/* Search Input — DS topbar style: gray-100 fill, no ring, subtle focus */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-zinc-500 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => localQuery.length > 0 && setShowDropdown(true)}
          placeholder="Поиск по всей системе..."
          className="w-full pl-9 pr-9 h-9 text-sm bg-gray-100 border border-transparent rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:bg-white focus:border-gray-300 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder-zinc-500 dark:focus:bg-zinc-800 dark:focus:border-zinc-700"
        />
        {localQuery && (
          <button
            onClick={() => {
              setLocalQuery('');
              clearSearch();
              setShowDropdown(false);
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-200 hover:bg-gray-200 dark:hover:bg-zinc-700"
            aria-label="Очистить"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {showDropdown && (localQuery.length > 0) && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-zinc-900 rounded-lg shadow-xl border border-gray-200 dark:border-zinc-800 max-h-96 overflow-y-auto">
          {isSearching && (
            <div className="p-4 text-center text-gray-500 dark:text-zinc-400">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-sm">Поиск...</p>
            </div>
          )}

          {!isSearching && (!results || results.length === 0) && (
            <div className="p-4 text-center text-gray-500 dark:text-zinc-400">
              <p className="text-sm">Ничего не найдено</p>
            </div>
          )}

          {!isSearching && results && results.length > 0 && (
            <>
              {results.slice(0, 10).map((result, index) => (
                <button
                  key={`${result.entityType}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/60 border-b border-gray-100 dark:border-zinc-800 last:border-b-0 transition-colors ${
                    selectedIndex === index ? 'bg-gray-50 dark:bg-zinc-800/60' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Preview Image */}
                    {result.preview && (
                      // Превью макета приходит адресом гейтвея, превью карточки
                      // маркетплейса — внешним CDN; различает AuthorizedMediaImage.
                      <AuthorizedMediaImage
                        src={result.preview}
                        alt={result.title}
                        thumbnailWidth={160}
                        className="w-12 h-12 object-cover rounded flex-shrink-0"
                      />
                    )}
                    {!result.preview && (
                      <div className="w-12 h-12 bg-gray-200 dark:bg-zinc-700 rounded flex-shrink-0 flex items-center justify-center">
                        <Search className="h-8 w-8 text-gray-400 dark:text-zinc-500" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                            ENTITY_TYPE_COLORS[result.entityType]
                          }`}
                        >
                          {ENTITY_TYPE_LABELS[result.entityType]}
                        </span>
                        {result.status && (
                          <span className="text-xs text-gray-500 dark:text-zinc-400">{result.status}</span>
                        )}
                      </div>
                      <p className="font-medium text-gray-900 dark:text-zinc-100 truncate">
                        {highlightMatch(result.title, localQuery)}
                      </p>
                      {result.subtitle && (
                        <p className="text-sm text-gray-600 dark:text-zinc-300 truncate">{result.subtitle}</p>
                      )}
                      {result.matchedField && result.matchedValue && (
                        <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">
                          <span className="font-medium">{FIELD_LABELS[result.matchedField] || result.matchedField}:</span>{' '}
                          {highlightMatch(result.matchedValue, localQuery)}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}

              {/* Show All Results Button */}
              {totalResults > 10 && (
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    if (onNavigate) {
                      onNavigate(`/search?q=${encodeURIComponent(localQuery)}`);
                    } else {
                      window.location.hash = `/search?q=${encodeURIComponent(localQuery)}`;
                    }
                  }}
                  className="w-full px-4 py-3 text-center text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 font-medium transition-colors"
                >
                  Показать все результаты ({totalResults})
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

